import React, { useCallback, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import TypingArea from '@/components/TypingArea';
import ScoreMeter from '@/components/ScoreMeter';
import WaveformViz from '@/components/WaveformViz';
import StepUpModal from '@/components/StepUpModal';
import StatusBadge from '@/components/StatusBadge';
import Logo from '@/components/Logo';
import { useScore } from '@/hooks/useScore';
import type { BiometricFeatures, KeystrokeEvent } from '@/types/keystroke';
import type { LoginPhase, StepUpMethod } from '@/types/auth';
import { apiClient } from '@/lib/api-client';
import { API_ENDPOINTS } from '@/types/api';

const LOGIN_PROMPT =
  'A autenticacao biometrica por digitacao e o futuro da seguranca digital, combinando conveniencia e protecao avancada.';

/**
 * Login page — Login by typing with ScoreMeter and phase states.
 */
export default function Login() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<LoginPhase>('typing');
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [showStepUp, setShowStepUp] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { displayScore, zone, setScore, color } = useScore();
  const featuresRef = useRef<BiometricFeatures | null>(null);

  const handleKeystroke = useCallback((_count: number, data: number[]) => {
    setWaveformData(data);
  }, []);

  const handleComplete = useCallback(
    async (_events: KeystrokeEvent[], features: BiometricFeatures) => {
      setPhase('analyzing');
      setIsLoading(true);
      featuresRef.current = features;

      try {
        const response = await apiClient.post<{
          tokens: { access_token: string; refresh_token: string; expires_in: number; token_type: string } | null;
          verification: { score: number; confidence: number; decision: string; zone: string; requires_step_up: boolean };
          user: unknown;
        }>(API_ENDPOINTS.AUTH_LOGIN, { features });

        const { verification, tokens } = response.data;
        // Backend returns score as 0-1, multiply by 100 for display
        const displayScoreValue = verification.score * 100;
        setScore(displayScoreValue);

        // Map backend decision to phase
        const decision = verification.decision;
        if (decision === 'accepted') {
          if (tokens) {
            apiClient.setTokens({
              accessToken: tokens.access_token,
              refreshToken: tokens.refresh_token,
              expiresIn: tokens.expires_in,
              tokenType: tokens.token_type,
            });
          }
          setPhase('accepted');
          setTimeout(() => navigate('/dashboard'), 1500);
        } else if (decision === 'gray_zone' && verification.requires_step_up) {
          setPhase('gray_zone');
          setShowStepUp(true);
        } else {
          setPhase('rejected');
        }
      } catch (err) {
        setPhase('rejected');
        setError(err instanceof Error ? err.message : 'Falha na autenticacao');
      } finally {
        setIsLoading(false);
      }
    },
    [navigate, setScore],
  );

  const handleStepUp = useCallback(
    async (method: StepUpMethod, payload: string) => {
      setIsLoading(true);
      try {
        await apiClient.post(API_ENDPOINTS.AUTH_STEP_UP, { method, payload });
        setShowStepUp(false);
        setPhase('accepted');
        setTimeout(() => navigate('/dashboard'), 1000);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Falha na verificacao');
      } finally {
        setIsLoading(false);
      }
    },
    [navigate, setScore],
  );

  const handleRetry = useCallback(() => {
    setPhase('typing');
    setScore(0);
    setError('');
    setWaveformData([]);
  }, [setScore]);

  const phaseConfig: Record<LoginPhase, { badge: string; variant: 'neutral' | 'brand' | 'info' | 'success' | 'warning' | 'danger' }> = {
    typing: { badge: 'Digite o texto abaixo', variant: 'neutral' },
    analyzing: { badge: 'Analisando assinatura...', variant: 'info' },
    accepted: { badge: 'Identidade confirmada', variant: 'success' },
    rejected: { badge: 'Assinatura nao reconhecida', variant: 'danger' },
    gray_zone: { badge: 'Verificacao adicional necessaria', variant: 'warning' },
    step_up: { badge: 'Verificacao em andamento', variant: 'info' },
  };

  const pc = phaseConfig[phase];

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12">
      {/* Header */}
      <div className="text-center mb-8 animate-fade-in">
        <div className="flex items-center justify-center mb-4">
          <Logo markSize="h-9" wordClass="text-lg" />
        </div>
        <h1 className="text-2xl font-bold text-txt-primary font-sans mb-2">
          Apenas digite para fazer o Login
        </h1>
        <StatusBadge
          label={pc.badge}
          variant={pc.variant}
          pulse={phase === 'analyzing'}
          size="md"
        />
      </div>

      {/* Score Meter */}
      {phase !== 'typing' && (
        <div className="w-full max-w-typing mb-6 animate-slide-up">
          <ScoreMeter score={displayScore} animate={true} />
        </div>
      )}

      {/* Typing Area */}
      <div className="w-full max-w-typing mb-6">
        <TypingArea
          promptText={LOGIN_PROMPT}
          onComplete={handleComplete}
          onKeystroke={handleKeystroke}
          disabled={phase !== 'typing'}
          placeholder="Digite aqui para se autenticar..."
          autoFocus={false}
        />
      </div>

      {/* Waveform */}
      {waveformData.length > 0 && (
        <div className="w-full max-w-typing mb-6">
          <WaveformViz
            data={waveformData}
            width={600}
            height={60}
            color={color}
            maxPoints={30}
            animate={true}
            showGrid={false}
          />
        </div>
      )}

      {/* Phase-specific UI */}
      {phase === 'accepted' && (
        <div className="text-center animate-slide-up p-6 bg-accent-success/10 border border-accent-success/30 rounded-card max-w-sm">
          <span className="text-4xl block mb-2">{'\u2713'}</span>
          <p className="text-accent-success font-semibold font-sans">
            Bem-vindo de volta!
          </p>
          <p className="text-xs text-txt-muted mt-1 font-sans">
            Redirecionando para o dashboard...
          </p>
        </div>
      )}

      {phase === 'rejected' && (
        <div className="text-center animate-slide-up p-6 bg-accent-danger/10 border border-accent-danger/30 rounded-card max-w-sm">
          <span className="text-4xl block mb-2">{'\u2717'}</span>
          <p className="text-accent-danger font-semibold font-sans mb-3">
            Assinatura nao reconhecida
          </p>
          <button
            onClick={handleRetry}
            className="bg-accent-brand hover:bg-accent-brand/90 text-white px-6 py-2 rounded-lg font-sans text-sm font-medium transition-colors"
          >
            Tentar Novamente
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <p className="text-sm text-accent-danger font-sans mt-4">{error}</p>
      )}

      {/* Links */}
      <div className="mt-8 flex items-center gap-4 text-sm text-txt-muted font-sans">
        <button
          onClick={() => navigate('/')}
          className="hover:text-txt-primary transition-colors"
        >
          Voltar ao inicio
        </button>
        <span className="text-bg-surface">&middot;</span>
        <button
          onClick={() => navigate('/onboarding')}
          className="hover:text-txt-primary transition-colors"
        >
          Criar conta
        </button>
      </div>

      {/* Step-up Modal */}
      <StepUpModal
        isOpen={showStepUp}
        methods={['retype', 'email_otp', 'totp']}
        onSubmit={handleStepUp}
        onClose={() => {
          setShowStepUp(false);
          setPhase('typing');
        }}
        isLoading={isLoading}
      />
    </div>
  );
}
